import { useState } from 'react';

import type { CoAttendantRole } from '../../types';
import type React from 'react';

/** Mapa role → capability que o gate visual exige (D3, 2 camadas). */
export const ROLE_CAPABILITY: Record<CoAttendantRole, string> = {
  shadow_observer: 'coattendance-shadow-observer',
  collaborative_observer: 'coattendance-collaborative-observer',
  supervisor_monitor: 'coattendance-invisible-to-all',
};

interface ModeOption {
  role: CoAttendantRole;
  label: string;
  sub: string;
  tone: 'neutral' | 'brand' | 'inverted';
}

const MODE_OPTIONS: ModeOption[] = [
  {
    role: 'shadow_observer',
    label: 'Visível ao atendente',
    sub: 'observador · padrão',
    tone: 'neutral',
  },
  {
    role: 'collaborative_observer',
    label: 'Público',
    sub: 'colaborador · cliente notificado',
    tone: 'brand',
  },
  {
    role: 'supervisor_monitor',
    label: 'Invisível',
    sub: 'invisível · exige justificativa',
    tone: 'inverted',
  },
];

const ROLE_SHORT: Record<CoAttendantRole, string> = {
  shadow_observer: 'Visível',
  collaborative_observer: 'Público',
  supervisor_monitor: 'Invisível',
};

export interface MonitoringModeSelectorProps {
  /** Role corrente da sessão (null enquanto resolve). */
  currentRole: CoAttendantRole | null;
  /** Predicado de capability (useCapability) — esconde os modos sem cap (D3). */
  hasCapability: (code: string) => boolean;
  isChanging: boolean;
  /**
   * Seleciona um modo. O parent decide o fluxo de `supervisor_monitor`
   * (abrir o modal de justificativa) — o seletor apenas emite o role.
   */
  onSelectRole: (role: CoAttendantRole) => void;
}

/**
 * MonitoringModeSelector — controle de modo no `acomp-header__actions` (D3).
 * Mostra SÓ os modos cuja capability o supervisor tem (esconder, não desabilitar
 * morto). Estilo consistente com `AcompanharMenu` (`.acomp-menu`). `PATCH role`
 * é disparado pelo parent via `onSelectRole`.
 */
export function MonitoringModeSelector({
  currentRole,
  hasCapability,
  isChanging,
  onSelectRole,
}: MonitoringModeSelectorProps): React.JSX.Element | null {
  const [open, setOpen] = useState(false);

  const available = MODE_OPTIONS.filter((o) => hasCapability(ROLE_CAPABILITY[o.role]));
  // Sem alternativas (só o modo padrão) → não oferece troca.
  if (available.length <= 1) return null;

  const currentLabel = currentRole !== null ? ROLE_SHORT[currentRole] : 'Modo';

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn-ghost-mini"
        disabled={isChanging}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        Modo: {currentLabel}
        {isChanging ? ' · trocando…' : ' ▾'}
      </button>
      {open && (
        <div
          className="acomp-menu"
          role="menu"
          style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20 }}
          onMouseLeave={() => {
            setOpen(false);
          }}
        >
          <ul className="acomp-menu__list">
            {available.map((o) => (
              <li key={o.role}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={o.role === currentRole}
                  className={`acomp-menu__item acomp-menu__item--${o.tone}`}
                  disabled={isChanging || o.role === currentRole}
                  onClick={() => {
                    setOpen(false);
                    onSelectRole(o.role);
                  }}
                >
                  <span className="acomp-menu__label">{o.label}</span>
                  <span className="acomp-menu__sub mono">{o.sub}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
